import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Users, Plus, Search, Shield, Key, UserX, UserCheck, Edit } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const ROLE_LABELS = {
  sales_executive:        "Sales Executive",
  sales_manager:          "Sales Manager",
  support_executive:      "Support Executive",
  verification_executive: "Verification Executive",
};
const ROLES = Object.keys(ROLE_LABELS);

// Permission module → checkboxes shown in the matrix editor.
const PERM_SCHEMA = {
  dashboard:    ["view"],
  leads:        ["view", "edit", "assign"],
  shops:        ["view", "verify", "approve", "reject", "assign", "recommend"],
  customers:    ["view"],
  subscription: ["view", "activate", "extend"],
  refer:        ["view"],
  whatsapp:     ["send"],
  reports:      ["view"],
};

function CreateStaffDialog({ open, onOpenChange, onDone }) {
  const [form, setForm] = useState({
    full_name: "", email: "", mobile: "", username: "", password: "",
    role: "sales_executive",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/staff", form);
      toast.success("Staff created");
      onOpenChange(false);
      setForm({ full_name: "", email: "", mobile: "", username: "", password: "", role: "sales_executive" });
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Platform Staff</DialogTitle>
          <DialogDescription>Internal HisaabPro team member — customer-facing shops team, NOT a shop employee.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Full name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="staff-c-name" /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-c-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="staff-c-email" /></div>
            <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required data-testid="staff-c-mobile" /></div>
            <div><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required data-testid="staff-c-username" /></div>
            <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="staff-c-password" /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="staff-c-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create staff"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PermissionsDialog({ open, onOpenChange, staff, onDone }) {
  const [perms, setPerms] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (staff) setPerms(JSON.parse(JSON.stringify(staff.permissions || {}))); }, [staff]);
  const toggle = (mod, act) => {
    setPerms((p) => {
      const next = { ...p, [mod]: { ...(p[mod] || {}) } };
      next[mod][act] = !next[mod][act];
      return next;
    });
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/staff/${staff.id}`, { permissions: perms });
      toast.success("Permissions saved");
      onOpenChange(false); onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  if (!staff) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Permissions — {staff.full_name}</DialogTitle>
          <DialogDescription>Role default: <b>{ROLE_LABELS[staff.role]}</b>. Override any checkbox below.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto" data-testid="staff-perm-grid">
          {Object.entries(PERM_SCHEMA).map(([mod, acts]) => (
            <div key={mod} className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="font-display font-bold text-sm text-slate-700 capitalize">{mod}</div>
              <div className="mt-2 space-y-1.5">
                {acts.map((a) => (
                  <label key={a} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!perms?.[mod]?.[a]}
                      onChange={() => toggle(mod, a)}
                      data-testid={`staff-perm-${mod}-${a}`}
                    />
                    <span className="capitalize">{a}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="staff-perm-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save permissions"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminStaff() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCounts, setTotalCounts] = useState({ total: 0, active: 0 });
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [permStaff, setPermStaff] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/staff");
      setRows(data.items || []);
      setTotalCounts({ total: data.total, active: data.active });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (sid, status) => {
    try {
      await api.patch(`/admin/staff/${sid}`, { status });
      toast.success(status === "active" ? "Activated" : "Deactivated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
  };

  const resetPw = async (sid, name) => {
    const pw = window.prompt(`New password for ${name} (min 6 chars):`);
    if (!pw || pw.length < 6) { pw && toast.error("Min 6 chars"); return; }
    try {
      await api.post(`/admin/staff/${sid}/reset-password`, { new_password: pw });
      toast.success("Password reset");
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.full_name || "").toLowerCase().includes(s)
        || (r.email || "").toLowerCase().includes(s)
        || (r.username || "").toLowerCase().includes(s)
        || (r.role || "").toLowerCase().includes(s);
  });

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="admin-staff-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-2.5">
            <Users className="w-7 h-7 text-emerald-600" /> Platform Staff
          </h1>
          <p className="text-slate-600 mt-1 text-sm">Internal HisaabPro team — Sales, Support, Verification executives. NOT shop employees.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9" data-testid="staff-search" />
          </div>
          <Button onClick={() => setCreateOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="staff-add-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Staff
          </Button>
        </div>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-md border border-slate-200 p-4"><div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Total staff</div><div className="mt-1 font-display text-2xl font-bold" data-testid="staff-count-total">{totalCounts.total}</div></div>
        <div className="rounded-md border border-emerald-100 bg-emerald-50 p-4"><div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">Active</div><div className="mt-1 font-display text-2xl font-bold text-emerald-700" data-testid="staff-count-active">{totalCounts.active}</div></div>
        <div className="rounded-md border border-slate-200 p-4"><div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Pending assignments</div><div className="mt-1 font-display text-2xl font-bold" data-testid="staff-count-pending">{rows.reduce((n, r) => n + (r.assigned_pending || 0), 0)}</div></div>
        <div className="rounded-md border border-amber-100 bg-amber-50 p-4"><div className="text-[11px] uppercase tracking-widest font-bold text-amber-700">Recommended</div><div className="mt-1 font-display text-2xl font-bold text-amber-700" data-testid="staff-count-recommended">{rows.reduce((n, r) => n + (r.recommended_count || 0), 0)}</div></div>
      </div>

      {/* Table */}
      <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500 tracking-widest">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th>Role</th>
              <th>Email / Username</th>
              <th>Mobile</th>
              <th>Queue</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={7} className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-slate-400" /></td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-500">No staff yet — click Add Staff to create the first one.</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`staff-row-${r.id}`}>
                <td className="px-4 py-3 font-medium">{r.full_name}</td>
                <td><span className="text-xs font-bold rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">{ROLE_LABELS[r.role] || r.role}</span></td>
                <td className="text-xs text-slate-600"><div>{r.email}</div><div className="text-slate-400 font-mono">@{r.username}</div></td>
                <td className="text-xs">{r.mobile}</td>
                <td className="text-xs"><div className="text-slate-600">P: {r.assigned_pending || 0}</div><div className="text-amber-600 font-semibold">R: {r.recommended_count || 0}</div></td>
                <td>
                  {r.status === "active" ? (
                    <span className="text-xs font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">Active</span>
                  ) : (
                    <span className="text-xs font-bold text-red-700 bg-red-50 rounded-full px-2 py-0.5">Inactive</span>
                  )}
                </td>
                <td className="pr-4 py-2 space-x-1">
                  <button onClick={() => setPermStaff(r)} title="Edit permissions" className="text-slate-600 hover:text-emerald-700 p-1.5 rounded hover:bg-slate-100" data-testid={`staff-perms-${r.id}`}><Shield className="w-4 h-4" /></button>
                  <button onClick={() => resetPw(r.id, r.full_name)} title="Reset password" className="text-slate-600 hover:text-slate-900 p-1.5 rounded hover:bg-slate-100" data-testid={`staff-resetpw-${r.id}`}><Key className="w-4 h-4" /></button>
                  {r.status === "active" ? (
                    <button onClick={() => setStatus(r.id, "inactive")} title="Deactivate" className="text-red-600 hover:text-red-800 p-1.5 rounded hover:bg-red-50" data-testid={`staff-deactivate-${r.id}`}><UserX className="w-4 h-4" /></button>
                  ) : (
                    <button onClick={() => setStatus(r.id, "active")} title="Activate" className="text-emerald-600 hover:text-emerald-800 p-1.5 rounded hover:bg-emerald-50" data-testid={`staff-activate-${r.id}`}><UserCheck className="w-4 h-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateStaffDialog open={createOpen} onOpenChange={setCreateOpen} onDone={load} />
      <PermissionsDialog open={!!permStaff} onOpenChange={(o) => !o && setPermStaff(null)} staff={permStaff} onDone={load} />
    </div>
  );
}
